import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { useMemo, useState } from "react";

import { SearchBar } from "@/features/directory/modules/search-bar/search-bar";
import { SERVICE_TYPE_GROUP_COPY } from "@/features/directory/utils/service-type-copy";
import { Section, ghostButton, sectionTitle } from "@/features/portal/components/portal-ui";
import { bookablePeople, nextOpenTime, peopleColumns } from "@/features/portal/modules/coaches/coach-directory";
import type { BookablePerson, BookableTrainer } from "@/features/portal/modules/coaches/coach-directory";
import { CoachPhoto } from "@/features/site/components/coach-photo";
import { SlotGateDialog } from "@/features/site/modules/open-calendar/slot-gate-dialog";
import type { SlotPick } from "@/features/site/types";
import { openTimeLabelIn } from "@/features/site/utils/labels";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { SERVICE_TYPE_GROUP, ServiceTypeGroup } from "@complete-athlete/enums";

/** People shown at once: one row on a wide screen, the rest are a search away. */
const SHOWN = 4;

const categoryChip =
  "inline-flex h-[34px] shrink-0 items-center rounded-full border border-border px-3.5 text-[13px] font-medium whitespace-nowrap text-muted-foreground transition-colors hover:bg-muted hover:text-foreground aria-pressed:border-foreground aria-pressed:bg-foreground aria-pressed:text-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none";

const inGroup = (person: BookablePerson, group: ServiceTypeGroup) =>
  person.serviceTypes.some((type) => SERVICE_TYPE_GROUP[type] === group);

/** A person in as little room as they fit: who they are, and their next open time one tap away. */
const PersonTile = ({ person, onPick }: { person: BookablePerson; onPick: (startAt: string) => void }) => {
  const { trainer, specialist } = person;
  const next = nextOpenTime(specialist);
  const when = next ? openTimeLabelIn(next, specialist.timeZone) : null;
  const about = [specialist.title || trainer.business.name, trainer.location.city].filter(Boolean).join(" · ");

  return (
    <article className="flex min-w-0 flex-col gap-3 rounded-3xl bg-card p-3.5 max-sm:w-[82%] max-sm:shrink-0 max-sm:snap-start">
      <Link
        to="/$trainerSlug"
        params={{ trainerSlug: trainer.slug }}
        className="group flex min-w-0 items-center gap-3 rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <CoachPhoto name={specialist.name} src={specialist.avatarUrl} className="size-[46px] rounded-2xl text-sm" />
        <span className="min-w-0">
          <b className="block truncate text-base font-medium tracking-[-0.02em] group-hover:underline">
            {specialist.name}
          </b>
          <span className="block truncate text-[13px] text-muted-foreground">{about}</span>
        </span>
      </Link>
      <div className="mt-auto flex items-center justify-between gap-2.5 rounded-2xl bg-[var(--site-well)] py-2 pr-2 pl-3.5">
        <div className="min-w-0">
          <span className="block text-xs text-muted-foreground">{m.landing_team_next()}</span>
          <b className="block truncate text-sm font-semibold">{when ?? m.landing_team_full()}</b>
        </div>
        {next && when ? (
          <button
            type="button"
            aria-label={m.landing_team_book({ name: specialist.name, when })}
            className="grid size-[34px] shrink-0 place-items-center rounded-full bg-primary text-primary-foreground outline-none transition-[filter,transform] hover:brightness-105 focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.96]"
            onClick={() => onPick(next)}
          >
            <ArrowUpRight aria-hidden className="size-4" />
          </button>
        ) : null}
      </div>
    </article>
  );
};

/**
 * The way from the front page to a specialist, in the room it needs: the search, the row of
 * categories, and a few people with their next open time. The search opens the results; a category
 * only narrows the people here. With nobody published it is the search alone, which still finds a
 * business by name.
 */
export const SpecialistSearch = ({ trainers }: { trainers: BookableTrainer[] }) => {
  const people = useMemo(() => bookablePeople(trainers), [trainers]);
  const [group, setGroup] = useState<ServiceTypeGroup | null>(null);
  const [pick, setPick] = useState<SlotPick | null>(null);

  const shown = (group ? people.filter((person) => inGroup(person, group)) : people).slice(0, SHOWN);
  const picked =
    people.find(
      (person) =>
        person.specialist.id === pick?.specialistId && person.trainer.business.organizationId === pick.organizationId,
    ) ?? null;

  return (
    <Section id="specialists" className="md:pt-[72px]">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 px-1">
        <h2 className={sectionTitle}>{m.portal_specialists_title()}</h2>
        <Link to="/search" className={ghostButton}>
          {m.portal_specialists_all()}
          <ArrowUpRight aria-hidden className="size-4" />
        </Link>
      </div>

      <div className="flex flex-col gap-3.5">
        <SearchBar />

        {people.length > 0 ? (
          <div className="flex max-w-full gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <button type="button" aria-pressed={group === null} className={categoryChip} onClick={() => setGroup(null)}>
              {m.portal_specialists_all_short()}
            </button>
            {Object.values(ServiceTypeGroup).map((entry) => (
              <button
                key={entry}
                type="button"
                aria-pressed={group === entry}
                className={categoryChip}
                onClick={() => setGroup(entry)}
              >
                {SERVICE_TYPE_GROUP_COPY[entry]()}
              </button>
            ))}
          </div>
        ) : null}

        {shown.length > 0 ? (
          <div
            className={cn(
              "flex snap-x snap-proximity gap-2.5 overflow-x-auto [scrollbar-width:none] sm:grid sm:grid-cols-2 sm:overflow-visible [&::-webkit-scrollbar]:hidden",
              peopleColumns(shown.length),
            )}
          >
            {shown.map((person) => (
              <PersonTile
                key={`${person.trainer.business.organizationId}:${person.specialist.id}`}
                person={person}
                onPick={(startAt) =>
                  setPick({
                    specialistId: person.specialist.id,
                    serviceId: person.specialist.serviceId,
                    startAt,
                    organizationId: person.trainer.business.organizationId,
                  })
                }
              />
            ))}
          </div>
        ) : people.length > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl bg-card p-4 text-muted-foreground">
            {m.portal_specialists_none()}
            <button type="button" className={ghostButton} onClick={() => setGroup(null)}>
              {m.portal_specialists_show_all()}
            </button>
          </div>
        ) : null}
      </div>

      <SlotGateDialog
        pick={pick}
        specialist={picked?.specialist ?? null}
        serviceName={picked?.serviceName ?? null}
        onClose={() => setPick(null)}
      />
    </Section>
  );
};
